import React, { useState, useContext, useEffect } from 'react';
import { NotificationContext } from './Dashboard';
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  ArrowUpward,
  ArrowDownward,
  SwapHoriz,
  ContentCopy,
  OpenInNew,
  CheckCircle,
  AccessTime,
  Error
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import TransactionHistory from './TransactionHistory';
import { getAllWalletTransactions, formatTransactionForDisplay } from '../utils/walletUtils';
import './Wallet.css';

const TransactionsPage = () => {
  const { showNotification } = useContext(NotificationContext);
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [stats, setStats] = useState({
    totalSent: 0,
    totalReceived: 0,
    totalSwapped: 0,
    totalTransactions: 0
  });

  // Load transactions from Supabase
  useEffect(() => {
    const loadTransactions = async () => {
      setIsLoading(true);
      try {
        // Use the demo wallet address for now
        // In a real app, this would be the user's connected wallet address
        const walletAddress = "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266";

        // Fetch transactions from Supabase
        const txList = await getAllWalletTransactions(walletAddress);

        if (txList && txList.length > 0) {
          // Format transactions for display
          const formattedTransactions = txList.map(tx =>
            formatTransactionForDisplay(tx, walletAddress)
          );

          setTransactions(formattedTransactions);
          calculateStats(formattedTransactions);
        } else {
          // If no transactions in Supabase, create a sample transaction
          const sampleTransaction = {
            id: "sample-tx-1",
            type: "receive",
            amount: "0.05",
            token: "ETH",
            timestamp: new Date().toISOString(),
            status: "completed",
            hash: "0xb0e93e9e48b6095775510ed5121f5862c96b330c7063c3cd5028fd7139f6c4f9",
            from: "0x15095Ec8FB1Fc9C664b3223459dFF43158ACe7aD",
            to: "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266",
            gasFee: "0.001",
            network: "Sepolia Testnet"
          };

          setTransactions([sampleTransaction]);
          calculateStats([sampleTransaction]);
        }
      } catch (error) {
        console.error("Error loading transactions:", error);
        showNotification("Error loading transactions", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadTransactions();
  }, [showNotification]);

  // Calculate transaction statistics
  const calculateStats = (txList) => {
    const stats = {
      totalSent: 0,
      totalReceived: 0,
      totalSwapped: 0,
      totalTransactions: txList.length
    };

    txList.forEach(tx => {
      if (tx.type === 'send') {
        stats.totalSent += parseFloat(tx.amount || 0);
      } else if (tx.type === 'receive') {
        stats.totalReceived += parseFloat(tx.amount || 0);
      } else if (tx.type === 'swap') {
        stats.totalSwapped += parseFloat(tx.fromAmount || 0);
      }
    });

    setStats(stats);
  };

  // Filter transactions based on search query, filter type, and time range
  const filteredTransactions = transactions.filter(tx => {
    // Search query filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      (tx.from && tx.from.toLowerCase().includes(searchLower)) ||
      (tx.to && tx.to.toLowerCase().includes(searchLower)) ||
      (tx.hash && tx.hash.toLowerCase().includes(searchLower)) ||
      (tx.token && tx.token.toLowerCase().includes(searchLower)) ||
      (tx.fromToken && tx.fromToken.toLowerCase().includes(searchLower)) ||
      (tx.toToken && tx.toToken.toLowerCase().includes(searchLower));

    // Transaction type filter
    const matchesType =
      filter === 'all' ||
      (filter === 'sent' && tx.type === 'send') ||
      (filter === 'received' && tx.type === 'receive') ||
      (filter === 'swapped' && tx.type === 'swap');

    // Time range filter
    const txDate = new Date(tx.timestamp);
    const now = new Date();
    const matchesTimeRange =
      timeRange === 'all' ||
      (timeRange === 'today' && txDate.toDateString() === now.toDateString()) ||
      (timeRange === 'week' && (now - txDate) < 7 * 24 * 60 * 60 * 1000) ||
      (timeRange === 'month' && (now - txDate) < 30 * 24 * 60 * 60 * 1000);

    return matchesSearch && matchesType && matchesTimeRange;
  });

  // Handle refresh button click
  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      // Use the demo wallet address for now
      const walletAddress = "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266";

      // Fetch fresh transactions from Supabase
      const txList = await getAllWalletTransactions(walletAddress);

      if (txList && txList.length > 0) {
        // Format transactions for display
        const formattedTransactions = txList.map(tx =>
          formatTransactionForDisplay(tx, walletAddress)
        );

        setTransactions(formattedTransactions);
        calculateStats(formattedTransactions);
        showNotification("Transactions refreshed", "success");
      } else {
        showNotification("No transactions found", "info");
      }
    } catch (error) {
      console.error("Error refreshing transactions:", error);
      showNotification("Error refreshing transactions", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification("Copied to clipboard", "success");
  };

  // Get transaction status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle fontSize="small" sx={{ color: '#00C853' }} />;
      case 'pending':
        return <AccessTime fontSize="small" sx={{ color: '#FFC107' }} />;
      case 'failed':
        return <Error fontSize="small" sx={{ color: '#F44336' }} />;
      default:
        return <AccessTime fontSize="small" sx={{ color: '#9E9E9E' }} />;
    }
  };

  // Get transaction type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'send':
        return <ArrowUpward fontSize="small" sx={{ color: '#F44336' }} />;
      case 'receive':
        return <ArrowDownward fontSize="small" sx={{ color: '#00C853' }} />;
      case 'swap':
        return <SwapHoriz fontSize="small" sx={{ color: '#2196F3' }} />;
      default:
        return null;
    }
  };

  return (
    <Box className="transactions-container" sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#00ffff' }}>
        Transactions
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            bgcolor: 'rgba(0, 30, 60, 0.7)',
            color: 'white',
            border: '1px solid rgba(0, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 255, 255, 0.1)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
                Total Transactions
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                {stats.totalTransactions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            bgcolor: 'rgba(0, 30, 60, 0.7)',
            color: 'white',
            border: '1px solid rgba(0, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 255, 255, 0.1)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
                Total Sent
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold', color: '#F44336' }}>
                {stats.totalSent.toFixed(4)} ETH
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            bgcolor: 'rgba(0, 30, 60, 0.7)',
            color: 'white',
            border: '1px solid rgba(0, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 255, 255, 0.1)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
                Total Received
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold', color: '#00C853' }}>
                {stats.totalReceived.toFixed(4)} ETH
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            bgcolor: 'rgba(0, 30, 60, 0.7)',
            color: 'white',
            border: '1px solid rgba(0, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 255, 255, 0.1)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="rgba(255, 255, 255, 0.7)">
                Total Swapped
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold', color: '#2196F3' }}>
                {stats.totalSwapped.toFixed(4)} ETH
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{
        p: 2,
        mb: 3,
        bgcolor: 'rgba(0, 30, 60, 0.7)',
        border: '1px solid rgba(0, 255, 255, 0.2)',
        borderRadius: 2
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by address, hash, or token..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  </InputAdornment>
                ),
                sx: {
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 255, 255, 0.7)',
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setFilter('all')}
                sx={{
                  color: filter === 'all' ? '#00ffff' : 'white',
                  borderColor: filter === 'all' ? '#00ffff' : 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { borderColor: '#00ffff', bgcolor: 'rgba(0, 255, 255, 0.1)' }
                }}
              >
                All
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowUpward />}
                onClick={() => setFilter('sent')}
                sx={{
                  color: filter === 'sent' ? '#00ffff' : 'white',
                  borderColor: filter === 'sent' ? '#00ffff' : 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { borderColor: '#00ffff', bgcolor: 'rgba(0, 255, 255, 0.1)' }
                }}
              >
                Sent
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowDownward />}
                onClick={() => setFilter('received')}
                sx={{
                  color: filter === 'received' ? '#00ffff' : 'white',
                  borderColor: filter === 'received' ? '#00ffff' : 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { borderColor: '#00ffff', bgcolor: 'rgba(0, 255, 255, 0.1)' }
                }}
              >
                Received
              </Button>
              <Button
                variant="outlined"
                startIcon={<SwapHoriz />}
                onClick={() => setFilter('swapped')}
                sx={{
                  color: filter === 'swapped' ? '#00ffff' : 'white',
                  borderColor: filter === 'swapped' ? '#00ffff' : 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { borderColor: '#00ffff', bgcolor: 'rgba(0, 255, 255, 0.1)' }
                }}
              >
                Swapped
              </Button>
              <Tooltip title="Refresh Transactions">
                <IconButton
                  onClick={handleRefresh}
                  sx={{
                    color: 'white',
                    '&:hover': { color: '#00ffff', bgcolor: 'rgba(0, 255, 255, 0.1)' }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Transaction History */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress sx={{ color: '#00ffff' }} />
        </Box>
      ) : (
        <TransactionHistory transactions={filteredTransactions} />
      )}
    </Box>
  );
};

export default TransactionsPage;
